import { fmtAge, tierLabel } from '@kmitl/shared';
import type { FallbackTier } from '@kmitl/shared';

interface PanelHeaderProps {
  /** Section title — rendered as eyebrow */
  title: string;
  /** Data age in minutes from NormalizedFeed.meta.ageMinutes */
  ageMinutes?: number | null;
  /** Fallback tier from NormalizedFeed.meta.fallbackTier */
  fallbackTier?: FallbackTier;
  /** Short source identifier e.g. "open-meteo" | "bma-ckan" */
  source?: string;
  /** Extra content on the right side (e.g. a refresh button) */
  actions?: React.ReactNode;
}

function ageClass(minutes?: number | null, tier?: FallbackTier): string {
  if (tier === 'unavailable') return 'data-age--crit';
  if (tier === 'scenario') return 'data-age--warn';
  if (minutes == null) return '';
  if (minutes < 5) return 'data-age--fresh';
  if (minutes < 30) return '';
  if (minutes < 120) return 'data-age--warn';
  return 'data-age--stale';
}

/**
 * Returns 'panel-hdr--stale' when the feed is overdue enough that a
 * governor scanning the room should notice. Threshold: 2 h or unavailable.
 */
function headerStalenessClass(minutes?: number | null, tier?: FallbackTier): string {
  if (tier === 'unavailable') return 'panel-hdr--stale';
  if (minutes != null && minutes >= 120) return 'panel-hdr--stale';
  return '';
}

/**
 * PanelHeader — standard section header for all dashboard panels.
 *
 * Shows the panel title (eyebrow style) on the left, and on the right:
 * - source tag (if provided)
 * - tier warning badge (if not live data)
 * - data-age freshness chip (color-coded by age)
 *
 * Based on "context and metadata for every indicator" from Taipei City Dashboard
 * pattern and UNDP digital twin framework.
 */
export function PanelHeader({ title, ageMinutes, fallbackTier, source, actions }: PanelHeaderProps) {
  const tier = fallbackTier ?? 'live';
  const tl = tierLabel(tier);

  return (
    <div className={`panel-hdr${headerStalenessClass(ageMinutes, tier) ? ` ${headerStalenessClass(ageMinutes, tier)}` : ''}`}>
      <span className="eyebrow mono">{title}</span>

      {/* The metadata cluster is one flex item, not four loose ones. In a narrow
          rail the cluster used to be squeezed against the title until the title
          collapsed to a one-character column ("INTEL FEED // CAMPUS" rendered
          36 px wide and 45 px tall at 1024 px). As a single item it wraps
          whole, so the title keeps its width and the chips move to the next
          line. */}
      <div className="panel-hdr__meta">
        {source && (
          <span className="panel-source-tag mono" title={`Data source: ${source}`}>
            {source.toUpperCase()}
          </span>
        )}

        {tl && (
          <span
            className={`panel-tier-warn mono${tier === 'unavailable' ? ' panel-tier-warn--crit' : ''}`}
            title={`Data quality: ${tier} — not live`}
          >
            {tl}
          </span>
        )}

        {(ageMinutes != null || tier === 'unavailable') && (
          <span
            className={`data-age mono ${ageClass(ageMinutes, tier)}`}
            title={`Last updated: ${fmtAge(ageMinutes)} · tier: ${tier}`}
          >
            {tier === 'unavailable' ? 'OFFLINE' : fmtAge(ageMinutes)}
          </span>
        )}

        {actions}
      </div>
    </div>
  );
}
